import { BaseGraphqlLayer } from '@core/base';
import { DocumentTypes } from '@introspection/documentTypes';
import { IMutation, IQuery, ISteelFabProductInput, ISteelFabProductType } from '@introspection/index';
import { Injectable } from '@rxdi/core';
import { deepCopy, importQuery } from '@rxdi/graphql-client';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export type Product = ISteelFabProductType;

@Injectable()
export class ProductService extends BaseGraphqlLayer {
  getProducts(): Observable<Product[]> {
    return this.watchQuery({
      // fetchPolicy: 'network-only',
      query: 'list-steelfab-products.query.graphql',
    }).pipe(map((res) => res.data.listSteelFabProducts));
  }

  getProductsByCategory(categorySlug: string): Observable<Product[]> {
    return this.query({
      query: 'list-steelfab-products.query.graphql',
      variables: { categorySlug },
    }).pipe(map((res) => res.data.listSteelFabProducts));
  }

  getFeaturedProducts(): Observable<Product[]> {
    return this.watchQuery({
      query: 'list-featured-steelfab-products.query.graphql',
    }).pipe(map((res) => res.data.listFeaturedSteelFabProducts));
  }

  getProductBySlug(slug: string): Observable<Product> {
    return this.query({
      query: 'find-steelfab-product.query.graphql',
      variables: { slug },
      // context: {
      //   fetchOptions: {
      //     method: 'GET', // Overrides POST for this specific request
      //   },
      // },
    }).pipe(map((res) => res.data.findSteelFabProductBySlug));
  }

  createProduct(productInput: ISteelFabProductInput): Observable<Product> {
    delete productInput['files'];
    return this.mutate({
      mutation: 'create-steelfab-product.mutation.graphql',
      variables: { productInput },
      update: (proxy, { data: { createSteelFabProduct } }) => {
        const query = importQuery<DocumentTypes>('list-steelfab-products.query.graphql');
        const data = proxy.readQuery<IQuery>({
          query,
        });

        if (data) {
          proxy.writeQuery({
            query,
            overwrite: true,
            data: { ...data, listSteelFabProducts: [...data.listSteelFabProducts, createSteelFabProduct] },
          });
        }
      },
    }).pipe(map((res) => res.data.createSteelFabProduct));
  }

  updateProduct(id: string, productInput: ISteelFabProductInput): Observable<Product> {
    delete productInput['files'];
    return this.mutate({
      mutation: 'update-steelfab-product.mutation.graphql',
      variables: { id, productInput },
      update: (proxy, { data: { updateSteelFabProduct } }) => {
        const query = importQuery<DocumentTypes>('list-steelfab-products.query.graphql');
        const data = deepCopy(
          proxy.readQuery<IQuery>({
            query,
          })
        );
        if (data) {
          const index = data.listSteelFabProducts.findIndex((v) => v.id === updateSteelFabProduct.id);
          const listSteelFabProducts = data.listSteelFabProducts;
          listSteelFabProducts[index] = updateSteelFabProduct;
          proxy.writeQuery({
            query,
            data: {
              listSteelFabProducts,
            },
          });
        }
      },
    }).pipe(map((res) => res.data.updateSteelFabProduct));
  }

  deleteProduct(id: string): Observable<Product> {
    return this.mutate({
      mutation: 'delete-steelfab-product.mutation.graphql',
      variables: { id },
      update: (proxy) => {
        const query = importQuery<DocumentTypes>('list-steelfab-products.query.graphql');
        const data = proxy.readQuery<IQuery>({
          query,
        });

        if (data) {
          proxy.writeQuery({
            query,
            data: { ...data, listSteelFabProducts: data.listSteelFabProducts.filter((v) => v.id !== id) },
          });
        }
      },
    }).pipe(map((res) => res.data.deleteSteelFabProduct));
  }
}
